import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    fetchLiveTrades,
    publicTapeTradeSchema,
    SPEED_LIVE_WS_BASE,
    type PublicTapeTrade,
} from '@/lib/api/speed';
import { usePageVisible } from '@/lib/hooks/usePageVisible';
import { type ConnectionState, useWebSocket } from '@/lib/ws';

/** Message type the tape socket uses; mirrors MsgType in speedLiveTapeManager. */
const LIVE_TRADE_MSG = 5;

/**
 * How many rows to seed. This is also the server's ceiling
 * (`LIVE_TAPE_MAX_LIMIT` in the factions speed manager clamps to 50), so asking
 * for more is silently the same request. The socket fills the scrollback
 * forward from here; reaching further back pages on `nextBefore`, which
 * `fetchNextPage` does.
 */
const SEED_LIMIT = 50;

/**
 * Rows kept in memory from the *stream*. Older ones fall off the end rather
 * than growing forever.
 *
 * Paged history is not subject to it. This cap exists so that a busy tape
 * nobody is reading cannot grow without bound; rows fetched because a viewer
 * scrolled to the end and asked for them are the opposite of that.
 */
const MAX_ROWS = 200;

/**
 * Backstop and correction path. The socket is the live path; this catches a
 * silently dead one, and it is how a streamed row gets repaired: a manual
 * close arrives with an estimated pnl and the reconciler's real number never
 * publishes, and a close missed during a reconnect leaves a row Open.
 */
const REFETCH_MS = 60_000;

/**
 * A hidden browser tab still receives every frame and renders it for nobody.
 * After this long hidden the socket is dropped; on return it reconnects and
 * reseeds, so the viewer sees the current tape, not a stale one.
 */
const HIDDEN_GRACE_MS = 30_000;

export interface LiveTapeState {
    trades: PublicTapeTrade[];
    connection: ConnectionState;
    isLoading: boolean;
    isError: boolean;
    /** The server says there is older history behind what is loaded. */
    hasMore: boolean;
    /** An older page is on its way. */
    isLoadingMore: boolean;
    /** Ask for the page behind the oldest row held. */
    loadMore: () => void;
}

/**
 * Which of two copies of the same trade to show. A trade only ever moves from
 * Open to one terminal status, and only the reconciled row has the final pnl,
 * so the choice is a lattice, not a race: no clocks, no request timestamps.
 * A seed served from a cache that predates a streamed close can therefore
 * never put the position back to Open, and a seed that carries the reconciled
 * number always replaces the socket's estimate.
 */
export function pickTapeRow(
    streamed: PublicTapeTrade,
    seeded: PublicTapeTrade,
): PublicTapeTrade {
    const streamedOpen = streamed.status === 'Open';
    const seededOpen = seeded.status === 'Open';
    if (streamedOpen !== seededOpen) return streamedOpen ? seeded : streamed;
    if (seededOpen) return seeded;
    // both terminal: the seed is the row as stored, unless it has no pnl yet
    return seeded.pnl !== null || streamed.pnl === null ? seeded : streamed;
}

/**
 * The public live tape: every trader's opens and closes, newest first.
 *
 * Seeded from `GET /factions/speed/live` and kept current by the public socket.
 * Both carry the same row shape, so a pushed trade and a seeded one merge
 * without a second code path.
 *
 * Where both have a copy of a trade, `pickTapeRow` decides by status and by
 * whether the pnl is final, never by which arrived later: the seed may come
 * from a cache older than the stream. So the seed refetch, the reconnect
 * reseed and the return from a hidden tab all repair whatever the stream got
 * wrong or missed, and never undo what it got right.
 *
 * `enabled` is load-bearing, not a convenience: a socket held open where nobody
 * is looking is a connection and a re-render per trade for nothing. The Live
 * Trades page passes `true` — a mounted route IS the thing being looked at —
 * and page visibility is folded in here regardless, so a backgrounded browser
 * tab still drops the connection and reseeds on return.
 *
 * `trader` narrows the seed to one adapter, server-side. It is the only filter
 * that gets to travel: the rest are predicates over the rows this returns (see
 * `filters.ts`), which works because they match a decent share of any window,
 * while one trader's rows among the last few hundred global trades is usually
 * none. The socket is NOT narrowed — it pushes every trader's rows to every
 * viewer — so a trader view still owes its own filtering to the predicate.
 */
export function useLiveTape(
    networkId: number,
    enabled: boolean,
    trader: string | null = null,
): LiveTapeState {
    const pageVisible = usePageVisible(HIDDEN_GRACE_MS);
    const active = enabled && pageVisible;

    const [streamed, setStreamed] = useState<PublicTapeTrade[]>([]);

    const seed = useInfiniteQuery({
        // the trader is part of the identity of this page, not a detail of it:
        // two traders must not share a cache entry
        queryKey: ['speed', 'live', networkId, trader],
        queryFn: async ({ pageParam, signal }) =>
            fetchLiveTrades({
                limit: SEED_LIMIT,
                before: pageParam as number | null,
                trader,
                signal,
            }),
        initialPageParam: null as number | null,
        // `nextBefore` is an `_id` cursor, never a page number: rows arrive
        // while someone reads, and an offset would show them a row twice or
        // skip one entirely.
        getNextPageParam: (last) =>
            last.hasMore &&
            last.nextBefore !== null &&
            last.nextBefore !== undefined
                ? last.nextBefore
                : undefined,
        enabled: active,
        staleTime: 15_000,
        refetchInterval: active ? REFETCH_MS : false,
        retry: false,
    });

    // Dropping the streamed rows when the tape goes inactive is deliberate: on
    // return the seed is authoritative and fresher than a list that stopped
    // updating. Switching trader is the same situation: what streamed in
    // belonged to the tape as a whole, and the new seed is the honest start.
    useEffect(() => {
        if (!active) setStreamed([]);
    }, [active]);

    useEffect(() => {
        setStreamed([]);
    }, [trader]);

    const push = useCallback((incoming: PublicTapeTrade[]) => {
        if (incoming.length === 0) return;
        setStreamed((previous) => {
            // Dedupe on the numeric id, never tradeHash: a row is inserted under
            // `pending:<uuid>` and re-keyed to its entry tx mid-lifecycle, so a
            // hash-keyed tape would show the same trade twice. A close replaces
            // the open it belongs to rather than stacking beneath it.
            const arriving = new Set(incoming.map((row) => row.id));
            const kept = previous.filter((row) => !arriving.has(row.id));
            // the server holds insertion order inside a flush window, so the
            // last trade of a batch is the newest and belongs at the head
            return [...incoming].reverse().concat(kept).slice(0, MAX_ROWS);
        });
    }, []);

    // The handlers are re-created on every render; keep the socket from re-binding.
    const pushRef = useRef(push);
    pushRef.current = push;
    const refetchRef = useRef(seed.refetch);
    refetchRef.current = seed.refetch;
    const hasSeededRef = useRef(false);
    hasSeededRef.current = seed.data !== undefined;

    const { state } = useWebSocket(
        active ? `${SPEED_LIVE_WS_BASE}/${networkId}` : null,
        {
            onMessage: (_, event) => {
                try {
                    const frame = JSON.parse(String(event.data));
                    if (frame?.type !== LIVE_TRADE_MSG) return;
                    // One frame carries every trade from the server's flush
                    // window, so a burst of trades costs one parse and one
                    // render rather than one each. Parse rather than trust:
                    // this socket is public and unauthenticated, and a
                    // malformed row must drop that row, not break the tape.
                    const batch = Array.isArray(frame.trades)
                        ? frame.trades
                        : [];
                    const rows: PublicTapeTrade[] = [];
                    for (const row of batch) {
                        const parsed = publicTapeTradeSchema.safeParse(row);
                        if (parsed.success) rows.push(parsed.data);
                    }
                    pushRef.current(rows);
                } catch {
                    // not JSON, or not a shape we know — ignore it
                }
            },
            onStateChange: (next) => {
                // Every trade pushed while the socket was down is missing from
                // the stream; the seed has them. Only after a first seed, so the
                // initial fetch is not cancelled and restarted by its own socket.
                if (next === 'connected' && hasSeededRef.current) {
                    void refetchRef.current();
                }
            },
        },
    );

    // Streamed rows lead; the seed supplies the history behind them. Both are
    // ordered newest-first, and `id` is a monotonic counter, so this stays
    // sorted. A trade present in both is shown once, in whichever copy
    // pickTapeRow prefers, in the streamed position.
    const seeded = seed.data?.pages.flatMap((page) => page.trades) ?? [];
    const seededById = new Map(seeded.map((row) => [row.id, row]));
    const streamedIds = new Set(streamed.map((row) => row.id));
    const trades = [
        ...streamed.map((row) => {
            const twin = seededById.get(row.id);
            return twin ? pickTapeRow(row, twin) : row;
        }),
        ...seeded.filter((row) => !streamedIds.has(row.id)),
        // Never below the default, never below what has been paged in.
    ].slice(0, Math.max(MAX_ROWS, seeded.length));

    return {
        trades,
        connection: state,
        isLoading: seed.isLoading,
        isError: seed.isError,
        hasMore: seed.hasNextPage,
        isLoadingMore: seed.isFetchingNextPage,
        loadMore: () => {
            if (seed.hasNextPage && !seed.isFetchingNextPage) {
                void seed.fetchNextPage();
            }
        },
    };
}
